import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, CircularProgress, Avatar, Alert } from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { useTheme, alpha } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import { portalBootstrap } from "../../api/clientPortal";
import {
  getClientPortalErrorMessage,
  isPortalUnavailableError,
} from "./clientPortalErrorMessage";

export default function ClientPortalShell({ children, title, subtitle }) {
  const theme = useTheme();
  const [params] = useSearchParams();
  const company = params.get("company") || "";
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(Boolean(company));
  const [unavailable, setUnavailable] = useState(false);
  const [unavailableMessage, setUnavailableMessage] = useState("");
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!company) {
      setLoading(false);
      setUnavailable(false);
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setUnavailable(false);
    setLoadError(null);
    portalBootstrap(company)
      .then((data) => {
        if (!cancelled) setBrand(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isPortalUnavailableError(err)) {
          setUnavailable(true);
          setUnavailableMessage(
            getClientPortalErrorMessage(err, {
              context: "bootstrap",
              fallback:
                "O portal ainda não está disponível para sua empresa. Peça ao suporte da empresa para habilitar o acesso.",
            })
          );
          return;
        }
        setLoadError(
          getClientPortalErrorMessage(err, { context: "bootstrap" })
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [company]);

  const gradientBg =
    theme.palette.mode === "dark"
      ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, #0b0b0f 45%, #111318 100%)`
      : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, #f6f7fb 45%, #eef2ff 100%)`;

  const showChildren = !loading && !unavailable && !loadError;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        background: gradientBg,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: "100%",
          maxWidth: 480,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
        }}
      >
        <Box sx={{ textAlign: "center", mb: 3 }}>
          {loading ? (
            <CircularProgress size={28} sx={{ mb: 1 }} />
          ) : brand?.logo ? (
            <Box
              component="img"
              src={brand.logo}
              alt={brand.name}
              sx={{ maxHeight: 44, mb: 1.5, objectFit: "contain" }}
            />
          ) : (
            <Avatar
              sx={{
                width: 48,
                height: 48,
                mx: "auto",
                mb: 1.5,
                bgcolor: "primary.main",
              }}
            >
              <SupportAgentIcon />
            </Avatar>
          )}
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {brand?.name && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {brand.name}
            </Typography>
          )}
        </Box>

        {unavailable ? (
          <Alert severity="info">{unavailableMessage}</Alert>
        ) : loadError ? (
          <Alert severity="warning">{loadError}</Alert>
        ) : null}

        {showChildren ? children : null}
      </Paper>
      <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", mt: 3 }}>
        © {new Date().getFullYear()} {brand?.name || "Xpro"}. Todos os direitos reservados.
      </Typography>
    </Box>
  );
}
